import {
  attendeeFieldsSchema,
  type AttendeeFields,
  type Event,
  type EventUserRsvp,
  type Spouse,
} from '@mr-website/schemas';
import type { z } from 'zod';
import type { LinkedSpouse } from './types';

export function isUserRegisteredForEvent(event: Pick<Event, 'user_rsvp'>): boolean {
  const status = event.user_rsvp?.status;
  return (
    status === 'Registered' || status === 'Unpaid' || status === 'Attended' || status === 'WalkIn'
  );
}

export function formatUserRsvpStatusLines(
  userRsvp: Pick<EventUserRsvp, 'has_spouse' | 'guest_count'>,
): string[] {
  const { has_spouse, guest_count } = userRsvp;
  const lines = ['Registered'];

  if (has_spouse && guest_count > 0) {
    lines.push(`Bringing spouse and ${guest_count} Guest${guest_count === 1 ? '' : 's'}`);
  } else if (has_spouse) {
    lines.push('Bringing spouse');
  } else if (guest_count > 0) {
    lines.push(`Bringing ${guest_count} Guest${guest_count === 1 ? '' : 's'}`);
  }

  return lines;
}

export function formatUserRsvpMessage(userRsvp: EventUserRsvp): string {
  const parts: string[] = ['You'];

  if (userRsvp.has_spouse) {
    parts.push('your spouse');
  }

  const guestCount = userRsvp.guest_count;
  if (guestCount > 0) {
    parts.push(`${guestCount} guest${guestCount === 1 ? '' : 's'}`);
  }

  if (parts.length === 1) {
    return "You have RSVP'd";
  }

  if (parts.length === 2) {
    return `${parts[0]} and ${parts[1]} have RSVP'd`;
  }

  return `${parts[0]}, ${parts[1]}, and ${parts[2]} have RSVP'd`;
}

export function createGuestId(): string {
  return crypto.randomUUID();
}

export function createEmptyAttendee(): AttendeeFields {
  return {
    first_name: '',
    last_name: '',
    send_invite: false,
  };
}

export function createEmptyGuest(): AttendeeFields & { id: string } {
  return { id: createGuestId(), ...createEmptyAttendee() };
}

export function parseFullName(fullName: string): { first_name: string; last_name: string } {
  const trimmed = fullName.trim();
  if (!trimmed) return { first_name: '', last_name: '' };

  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return { first_name: parts[0], last_name: '' };

  return {
    first_name: parts[0],
    last_name: parts.slice(1).join(' '),
  };
}

export function formatFullName(firstName: string, lastName: string): string {
  return [firstName.trim(), lastName.trim()].filter(Boolean).join(' ');
}

export function linkedSpouseFromProfile(spouse?: Spouse | null): LinkedSpouse | null {
  if (!spouse?.first_name?.trim()) return null;

  return {
    contact_id: spouse.contact_id?.trim() || undefined,
    has_website_account: spouse.has_website_account,
    first_name: spouse.first_name.trim(),
    last_name: spouse.last_name?.trim() ?? '',
    email: spouse.email_address?.trim() || undefined,
  };
}

/** Prefer profile spouse data; fall back to event RSVP spouse for contact_id/email. */
export function resolveLinkedSpouse(
  profileSpouse?: Spouse | null,
  userRsvpSpouse?: EventUserRsvp['spouse'],
): LinkedSpouse | null {
  const fromProfile = linkedSpouseFromProfile(profileSpouse);
  if (!fromProfile) return null;

  if (!userRsvpSpouse) return fromProfile;

  const rsvpFirst = userRsvpSpouse.first_name.trim();
  const rsvpLast = userRsvpSpouse.last_name.trim();
  const namesMatch = rsvpFirst === fromProfile.first_name && rsvpLast === fromProfile.last_name;

  if (!namesMatch) return fromProfile;

  return {
    ...fromProfile,
    contact_id: (fromProfile.contact_id ?? userRsvpSpouse.contact_id?.trim()) || undefined,
    email: (fromProfile.email ?? userRsvpSpouse.email?.trim()) || undefined,
  };
}

export function spouseHasOwnAccount(linkedSpouse: LinkedSpouse | null): boolean {
  return linkedSpouse?.has_website_account === true;
}

export type FieldErrors = Partial<Record<keyof AttendeeFields, string>>;

export function attendeeFieldErrors(
  attendee: AttendeeFields,
  schema: z.ZodTypeAny = attendeeFieldsSchema,
): FieldErrors {
  const result = schema.safeParse(attendee);
  if (result.success) return {};

  const errors: FieldErrors = {};
  for (const issue of result.error.issues) {
    const key = issue.path[0];
    if (typeof key === 'string' && !errors[key as keyof AttendeeFields]) {
      errors[key as keyof AttendeeFields] = issue.message;
    }
  }
  return errors;
}

export function attendeeFieldErrorFor(
  attendee: AttendeeFields,
  field: keyof AttendeeFields,
  schema: z.ZodTypeAny = attendeeFieldsSchema,
): string | undefined {
  return attendeeFieldErrors(attendee, schema)[field];
}
